import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { GLASS_PANEL } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';

/**
 * The page's sections as one row of tabs (scrolls sideways on phones, sticks
 * under the top bar on wide screens) and, for sections with several parts,
 * a second row for the part.
 */
const ProfileNav = ({ groups, activeGroup, activeTab, onSelectGroup, onSelectTab, badges }) => {
  const { t } = useTranslation();
  const listRef = useRef(null);

  // Seçili sekme yatay kaydırmada görünür kalsın (sayfa dikeyde kaymadan)
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector('[aria-selected="true"]');
    if (!list || !active) return;
    const left = active.offsetLeft - list.offsetLeft;
    if (left < list.scrollLeft || left + active.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: Math.max(0, left - 16), behavior: 'smooth' });
    }
  }, [activeGroup]);

  const group = groups.find((item) => item.key === activeGroup);
  const subTabs = group?.tabs && group.visibleTabs.length > 1 ? group.visibleTabs : null;

  return (
    // Alt sekmeler aynı cam panelin içinde: yapışkanken alttaki içerik görünmesin
    <nav aria-label={t('profilePage.nav.label')} className={`${GLASS_PANEL} tw-rounded-[1.4rem] tw-p-1.5 lg:tw-sticky lg:tw-top-[5.5rem] lg:tw-z-30`}>
      <div ref={listRef} role="tablist" aria-label={t('profilePage.nav.label')} className="tw-flex tw-gap-1 tw-overflow-x-auto tw-overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
        {groups.map((item) => {
          const Icon = item.icon;
          const selected = item.key === activeGroup;
          const badge = badges[item.key] || 0;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelectGroup(item.key)}
              className={`tw-relative tw-flex tw-h-10 tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-bg-transparent tw-px-3.5 tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                selected ? 'tw-text-white' : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white'
              }`}
            >
              {selected && (
                <motion.span
                  layoutId="profile-nav-pill"
                  transition={{ type: 'spring', stiffness: 420, damping: 36 }}
                  className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-500 tw-to-cyan-600 tw-shadow-md tw-shadow-teal-600/30"
                />
              )}
              <Icon aria-hidden="true" className="tw-relative tw-h-4 tw-w-4" />
              <span className="tw-relative tw-whitespace-nowrap">{t(`profilePage.nav.groups.${item.key}`)}</span>
              {badge > 0 && (
                <span className={`tw-relative tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none ${selected ? 'tw-bg-white tw-text-teal-700' : 'tw-bg-rose-500 tw-text-white'}`}>
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {subTabs && (
        <div
          role="tablist"
          aria-label={t(`profilePage.nav.groups.${activeGroup}`)}
          className="tw-mt-1.5 tw-flex tw-gap-1 tw-overflow-x-auto tw-border-t tw-border-slate-200/70 tw-px-1 tw-pt-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden dark:tw-border-white/10"
        >
          {subTabs.map((tab) => {
            const selected = tab === activeTab;
            const badge = badges[tab] || 0;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => onSelectTab(tab)}
                className={`tw-flex tw-h-8 tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-text-[13px] tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                  selected
                    ? 'tw-bg-teal-500/[0.12] tw-text-teal-800 dark:tw-bg-teal-400/15 dark:tw-text-teal-200'
                    : 'tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-900 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white'
                }`}
              >
                <span className="tw-whitespace-nowrap">{t(`profilePage.nav.tabs.${tab}`)}</span>
                {badge > 0 && <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white">{badge}</span>}
              </button>
            );
          })}
        </div>
      )}
    </nav>
  );
};

ProfileNav.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.string),
    visibleTabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  activeGroup: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
  onSelectGroup: PropTypes.func.isRequired,
  onSelectTab: PropTypes.func.isRequired,
  badges: PropTypes.object.isRequired,
};

export default ProfileNav;
